import { buildPersonNode } from 'utils/schema/personSchema';
import { trailingSlash } from 'utils/strings';

const SITE = 'https://prismatic.io';
const ORGANIZATION_ID = `${SITE}/#organization`;

interface ImageLike {
  url?: string;
  file?: { url?: string };
}

export interface ArticleSchemaInput {
  title?: string | null;
  summary?: string | null;
  url?: string | null;
  publishDate?: string | null;
  lastUpdated?: string | null;
  featuredImage?: ImageLike | null;
  author?: { value?: { data?: AuthorReference } } | null;
  tags?: string[] | null;
}

const absoluteUrl = (path: string) => trailingSlash(`${SITE}${path.startsWith('/') ? '' : '/'}${path}`);

// Builder stores publish dates as plain strings; emit ISO 8601 when parseable
// (what Google expects for date* fields) and fall back to the raw value.
const toIso = (value?: string | null) => {
  if (!value) {
    return undefined;
  }
  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
};

const imageUrl = (image?: ImageLike | null) => image?.url || image?.file?.url || undefined;

/**
 * Builds Article-family + Person structured data from the fields a content
 * template already loads (title, summary, author, publishDate, image).
 * Generating in code populates every entry at once instead of hand-authoring
 * JSON-LD per Builder entry. Used by blog posts (Article), press releases
 * (NewsArticle) and customer stories (Article). Returns null when the minimum
 * fields are missing. Optional date/author/keyword fields are omitted when
 * absent, so a partial entry still yields valid (if leaner) markup.
 */
export const getArticleSchema = (
  data?: ArticleSchemaInput | null,
  articleType: 'Article' | 'NewsArticle' = 'Article',
) => {
  if (!data?.title || !data?.url) {
    return null;
  }

  const url = absoluteUrl(data.url);
  const author = buildPersonNode(data.author?.value?.data);
  const image = imageUrl(data.featuredImage);
  const datePublished = toIso(data.publishDate);
  const keywords = (data.tags || []).filter(Boolean);

  return {
    '@context': 'https://schema.org',
    '@type': articleType,
    headline: data.title,
    ...(data.summary ? { description: data.summary } : {}),
    ...(image ? { image } : {}),
    ...(datePublished ? { datePublished } : {}),
    dateModified: toIso(data.lastUpdated) || datePublished,
    ...(author ? { author } : {}),
    publisher: { '@id': ORGANIZATION_ID },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    ...(keywords.length ? { keywords } : {}),
  };
};
